import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import Helpers from '../../js/helpers';
import projectConfig from '../../js/config/_global';
import { COLLECTION_ORDER_BY, ORDER_BY_DIRECTION, RESPONSE, COOKIE_KEYS } from '../../js/_constants';
import api from '../../api';

// Slice Imports
import searchStore from './searchStore';
import userStore from './userStore';
import playlistStore from './playlistStore';

export type AlertMessage = {
    display: boolean;
    isAutoDisappearing: boolean;
    message: string;
    type: string;
    onClick?: (...args: any) => any;
    onClose?: (...args: any) => any;
    actionButtonText?: string;
    position?: string;
};

interface MainState {
    alert: AlertMessage;
    authenticated: boolean;
    collectionsOrder: { orderBy: string; order: string };
    countries: any[];
    gridStyle: string;
    loading: boolean;
    pageName: string;
    isSearchBarOpen: boolean;
    selectedMediaItems: any[];
    moduleTypes: any[];
    isGlobalLoading: boolean;
    selectedMediaItem: number;
    isModalOpen: boolean;
    faqs: any[];
    userRoles: string[];

    // Actions
    initApp: () => Promise<void>;
    refreshCoreData: (args?: { isBackgroundRequest: boolean }) => Promise<void>;
    getFAQs: (language: string) => Promise<void>;
    setAlert: (alert: Partial<AlertMessage>) => void;
    hideAlert: () => void;
    resetAlert: () => void;
    setGridStyle: (style: string) => void;
    setAuthenticated: (val: boolean) => void;
    setUserRoles: (roles: string[]) => void;
    setPageName: (name: string) => void;
    setModalOpen: (open: boolean) => void;
    setSelectedMediaItems: (items: any[]) => void;
    setLoading: (loading: boolean) => void;
    setIsSearchBarOpen: (isOpen: boolean) => void;
    setIsGlobalLoading: (isGlobalLoading: boolean) => void;
}

const initialAlert: AlertMessage = {
    display: false,
    message: '',
    type: 'info',
    isAutoDisappearing: true
};

const useGeneralStore = create<MainState>()((set, get) => ({
    // Initial State
    alert: { ...initialAlert, type: 'warning' },
    authenticated: Helpers.isAuthenticated(),
    collectionsOrder: {
        orderBy: COLLECTION_ORDER_BY.DATE,
        order: ORDER_BY_DIRECTION.DESC
    },
    countries: [],
    gridStyle: Helpers.getCookie('grid-style') || projectConfig.defaultMediaStyle,
    loading: false,
    pageName: '',
    isSearchBarOpen: false,
    selectedMediaItems: [],
    moduleTypes: [],
    isGlobalLoading: true,
    selectedMediaItem: -1,
    isModalOpen: false,
    faqs: [],
    userRoles: [],

    // Logic Actions
    initApp: async () => {
        set({ isGlobalLoading: true });
        try {
            const token = Helpers.getCookie(COOKIE_KEYS.TOKEN);
            if (token) {
                const jwtDecodedToken = jwtDecode(token as any) as any;
                set({ userRoles: jwtDecodedToken['cognito:groups'] || [] });
            }

            // Orchestrate other slices
            await Promise.all([
                searchStore.getState().getCarModelsAndCarTypes(),
                userStore.getState().getDealershipGroups()
            ]);
            
            set({ isGlobalLoading: false });
        } catch (error) {
            set({ isGlobalLoading: false });
        }
    },

    refreshCoreData: async (args) => {
        if (!args?.isBackgroundRequest) set({ isGlobalLoading: true });
        
        // Trigger action in playlist slice
        await playlistStore.getState().getPlaylistsRequest();
        
        set({ isGlobalLoading: false });
    },

    setIsGlobalLoading: (isGlobalLoading) => set({ isGlobalLoading }),  

    getFAQs: async (language) => {
        const response = await api.fetchFAQs(language);
        set({ faqs: response[RESPONSE.EXPAND_COLLECTION] });
    },

    // UI Setters
    setAlert: (alert) => set((state) => ({ alert: { ...state.alert, ...alert } })),
    hideAlert: () => set((state) => ({ alert: { ...state.alert, display: false } })),
    resetAlert: () => set({ alert: initialAlert }),
    setAuthenticated: (authenticated) => set({ authenticated }),
    setModalOpen: (isModalOpen) => set({ isModalOpen }),
    setUserRoles: (userRoles) => set({ userRoles }),
    
    setGridStyle: (gridStyle) => {
        Helpers.setCookie('grid-style', gridStyle, 24 * 365);
        set({ gridStyle });
    },

    setPageName: (pageName:any) => {
        const nextPageName = pageName.length > 50 ? `${pageName.substring(0, 50)}...` : pageName;
        set({ pageName: nextPageName });
    },
    setSelectedMediaItems: (selectedMediaItems) => set({ selectedMediaItems }),
    setLoading: (loading) => set({ loading }),
    setIsSearchBarOpen: (isSearchBarOpen) => set({ isSearchBarOpen })
}));

export default useGeneralStore;
